/**
 * Screen Service: Manages Wake Lock and Orientation APIs for forehead gameplay.
 */

export class ScreenService {
  private wakeLockSentinel: WakeLockSentinel | null = null;
  private isWakeLockRequested = false;

  constructor() {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible" && this.isWakeLockRequested) {
        void this.acquireWakeLock();
      }
    });
  }

  public async enableForeheadMode(): Promise<void> {
    this.isWakeLockRequested = true;
    await this.acquireWakeLock();
    await this.lockLandscape();
  }

  public disableForeheadMode(): void {
    this.isWakeLockRequested = false;
    this.releaseWakeLock();
    this.unlockOrientation();
  }

  private async acquireWakeLock(): Promise<void> {
    if (!("wakeLock" in navigator)) return;
    try {
      this.wakeLockSentinel = await navigator.wakeLock.request("screen");
      this.wakeLockSentinel.addEventListener("release", () => {
        this.wakeLockSentinel = null;
      });
    } catch {
      // Browser or battery settings may prevent wake lock
    }
  }

  private releaseWakeLock(): void {
    if (this.wakeLockSentinel) {
      void this.wakeLockSentinel.release();
      this.wakeLockSentinel = null;
    }
  }

  private async lockLandscape(): Promise<void> {
    if ("orientation" in screen && typeof (screen.orientation as unknown as { lock?: (o: string) => Promise<void> }).lock === "function") {
      try {
        await (screen.orientation as unknown as { lock: (o: string) => Promise<void> }).lock("landscape");
      } catch {
        // Not all mobile browsers support orientation locking (e.g. iOS Safari)
      }
    }
  }

  private unlockOrientation(): void {
    if ("orientation" in screen && typeof (screen.orientation as unknown as { unlock?: () => void }).unlock === "function") {
      try {
        (screen.orientation as unknown as { unlock: () => void }).unlock();
      } catch {
        // Fallback silently
      }
    }
  }

  public triggerHaptic(durationMs: number): void {
    if (durationMs > 0 && "vibrate" in navigator) {
      try {
        navigator.vibrate(durationMs);
      } catch {
        // Haptics unsupported or disabled
      }
    }
  }
}
